import * as React from 'react'
import { DiagramEngine, PortWidget } from '@projectstorm/react-diagrams-core'
import { BackendSystemsNodeModel } from './backend-systems-node-model'
import { css, cx } from '@emotion/css'
import { BackendSystemItem, containerHeight } from '../../backend-system-item'
import { List } from 'react-virtualized'
import { buildBackendSystemPortName } from './index'
import {
  componentsColumnWidth,
  diagramHeight,
  getColumnContainerStyle
} from '../../index'
import { LegacyGridSpinner } from '../../../../legacy-design-system/legacy-grid'
import { FlowMapEmptyColumn } from '../../flow-map-empty-column'

const itemSeparatorHeight = 10
export const itemHeight = containerHeight + itemSeparatorHeight

interface Props {
  readonly node: BackendSystemsNodeModel
  readonly engine: DiagramEngine
}

interface State {
  readonly selectedHash?: string
}

export class BackendSystemsNodeWidget extends React.Component<Props, State> {
  public constructor(props) {
    super(props)

    this.state = {
      selectedHash: undefined
    }
  }

  public render() {
    const { node, engine } = this.props
    const { selectedHash } = this.state

    const { items, onRowsRendered, isLoading } = node
    const tooltipBuffer = 50

    return (
      <div
        className={cx(
          getColumnContainerStyle(componentsColumnWidth, 0),
          css`
            cursor: default;
            align-items: center;
          `
        )}
      >
        {isLoading ? (
          <LegacyGridSpinner data-testid="backend-systems-loading-indicator" />
        ) : !Array.isArray(items) || items.length === 0 ? (
          <div
            className={css`
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <FlowMapEmptyColumn
              titleI18n={'title_applicationFlowMap_noBackendSystems'}
              descriptionI18n={'detail_applicationFlowMap_noBackendSystems'}
            />
          </div>
        ) : (
          <List
            className={css`
              outline: none;
            `}
            height={diagramHeight}
            width={componentsColumnWidth}
            overscanRowCount={0}
            rowCount={items.length}
            rowHeight={this.getAppropriateHeight(
              items.length,
              itemHeight,
              245,
              tooltipBuffer
            )}
            rowRenderer={({ style, index: rowIndex, key, isScrolling }) => {
              if (isScrolling) {
                return <div style={style} key={key} />
              }

              const portInName = buildBackendSystemPortName(rowIndex, 'in')
              const portOutName = buildBackendSystemPortName(rowIndex, 'out')

              const portIn = node.getPort(portInName)
              const portOut = node.getPort(portOutName)

              const { type, location, port, vendorName, hash } = items[rowIndex]

              return (
                <div
                  style={style}
                  key={key}
                  className={css`
                    display: flex;
                    justify-content: center;
                  `}
                >
                  <div
                    className={css`
                      display: flex;
                      justify-content: center;
                    `}
                  >
                    <div
                      className={css`
                        position: relative;
                        margin-bottom: ${itemSeparatorHeight}px;
                      `}
                    >
                      {!!portIn && (
                        <PortWidget
                          engine={engine}
                          port={portIn}
                          className={css`
                            position: absolute;
                            top: 50%;
                            left: 50%;
                          `}
                        />
                      )}
                      {!!portOut && (
                        <PortWidget
                          engine={engine}
                          port={portOut}
                          className={css`
                            position: absolute;
                            top: 50%;
                            right: 50%;
                          `}
                        />
                      )}

                      <BackendSystemItem
                        systemType={type}
                        location={location}
                        port={port}
                        vendorName={vendorName}
                        marginTop={this.getAppropriateHeight(
                          items.length,
                          0,
                          95,
                          tooltipBuffer
                        )}
                        isSelected={selectedHash === hash}
                        onClick={() => this.onClickComponent(hash)}
                      />
                    </div>
                  </div>
                </div>
              )
            }}
            onRowsRendered={({
              overscanStartIndex,
              overscanStopIndex,
              startIndex,
              stopIndex
            }) => {
              // eslint-disable-next-line no-console
              console.log(
                `onRowsRendered widget components: ${startIndex} - ${stopIndex} ${overscanStartIndex} - ${overscanStopIndex}`
              )

              onRowsRendered(startIndex, stopIndex, 'COMPONENTS')
            }}
          />
        )}
      </div>
    )
  }

  private onClickComponent(hash: string) {
    const { node } = this.props
    const { selectedHash } = this.state

    const { onClickComponent } = node

    // set internal state for getting the item border colored
    this.setState({
      selectedHash: selectedHash === hash ? undefined : hash
    })

    // trigger re-generate links for getting lines colored
    onClickComponent(hash)
  }

  private getAppropriateHeight(
    arrayLegth: number,
    defaultSize: number,
    newSize: number,
    tooltipBuffer: number
  ): number {
    return arrayLegth > 1
      ? defaultSize + tooltipBuffer
      : newSize + tooltipBuffer
  }
}
